/**
 * The conversation loop: prefetch, generate, tool call, inject, resume.
 *
 * "Resume" is a convenient lie worth naming. The model never continues
 * mid-stream. The harness appends the tool result and calls generate again
 * from the top. What makes that cheap is the prefix KV cache: the prior
 * context is already computed, so only the new tokens are prefilled. It
 * behaves like resuming and costs almost nothing.
 *
 * INTERRUPT AND JUSTIFY (Free's hypothesis, 2026-07-26) is implemented here as
 * a switchable arm rather than adopted on faith.
 *
 * Standard ReAct reasons BEFORE the model knows it wants a tool, so the
 * reasoning is often generic filler. Free's inversion interrupts AFTER the
 * model has committed: catch "search:" before the query exists, make the model
 * state why it is reaching for the tool, and only then let it write the
 * argument. Because transformers condition on their own prior tokens, a
 * justification that names the actual need should pull the query toward that
 * need.
 *
 * It also reconciles with standing directive D1 instead of fighting it. We
 * have receipts that thinking-on hurts memory work. This is not thinking-on.
 * It is a few tokens at one decision point, so the operative variable is
 * LOCALITY of reasoning rather than quantity.
 *
 * The failure mode to watch is post-hoc rationalization: once committed, a
 * model may invent a reason for a call it should not make. Arm C exists for
 * exactly that, giving the model an explicit way out.
 */

import type { MindClient } from "./llm/client";
import type { ChatMessage, GenStats } from "./llm/protocol";
import type { EmbedClient } from "./lattice/embedClient";
import type { LoadedPack } from "./lattice/packStore";
import { openAperture, DEFAULT_K, type ApertureResult } from "./lattice/aperture.ts";
import {
  parseToolCall,
  tagStopStrings,
  recordsStopPrefixes,
  formatSearchResults,
  EMPTY_SENTINEL,
  type Dialect,
  type BannerMode,
} from "./pure/toolParse.ts";
import { prompt as renderPrompt } from "./pure/promptKit.ts";
import { shapeQuery } from "./pure/queryShaping.ts";
import { fitToBudget, type FitReport } from "./contextFit.ts";
import type { BudgetedMsg } from "./pure/contextBudget.ts";
import { MAX_CONTEXT_TOKENS } from "./llm/catalog.ts";

export type JustifyArm =
  /** A: the model writes the whole call, we run it. */
  | "direct"
  /** B: interrupt at the prefix, demand a reason, then let it write the query. */
  | "justify"
  /** C: as B, but "no tool needed" is an explicit, legal outcome. */
  | "justify-escape";

export type ConverseConfig = {
  dialect: Dialect;
  arm: JustifyArm;
  /** Search once on the raw user turn before generating anything. */
  prefetch: boolean;
  k: number;
  maxToolCalls: number;
  enableThinking: boolean;
  maxNewTokens: number;
  /**
   * Search for this instead of for the user's turn. Left unset for ordinary
   * chat, where the turn IS the question. The debate sets it to the topic.
   */
  queryOverride?: string;
  /**
   * Force a banner wording instead of gating on recall strength.
   *
   * Exists for the A/B. The hedged wording is only safe to adopt everywhere if
   * it costs no grounding, and that is a measurement rather than an opinion:
   * Era 2 holds that what moves a small model is how hard the text binds it to
   * the retrieved facts, so loosening the words is exactly the kind of change
   * that has previously cost grounding here.
   */
  bannerMode?: BannerMode;
  /**
   * Speak as this instead of as the pack's baked-in identity.
   *
   * The pack stays the source of truth and these stay unset for ordinary chat.
   * They exist because the lab needs to tune a persona without re-exporting a
   * lattice, and the prompt registry cannot do it: its active set is global, so
   * a preamble edited there would apply to BOTH speakers and collapse them into
   * one voice, which is the exact failure the wrapper design was built to
   * prevent. Per-speaker text needs a per-call channel.
   */
  preamble?: string;
  persona?: string;
  /**
   * Override RELEVANCE_FLOOR for this call.
   *
   * A dial rather than a new default because the measurement that condemns 0.45
   * rests on n=30 from a single lattice. See the note on RELEVANCE_FLOOR.
   */
  floor?: number;
  /**
   * Let the answer CONTINUE the speaker's note rather than start a new turn.
   *
   * Off by default: it changes the token stream the model sees on every answer,
   * and the grounding cost or benefit is unmeasured. The lab toggles it so the
   * exact-prompt view can show both.
   */
  openPrefill?: boolean;
};

export const DEFAULT_CONFIG: ConverseConfig = {
  dialect: "records",
  arm: "direct",
  prefetch: true,
  k: DEFAULT_K,
  maxToolCalls: 2,
  enableThinking: false, // D1
  maxNewTokens: 512,
};

export type ConverseEvent =
  | { type: "prefetch"; result: ApertureResult }
  | { type: "token"; text: string }
  | { type: "justification"; text: string }
  | { type: "toolCall"; query: string }
  | { type: "toolResult"; result: ApertureResult }
  | { type: "budget"; report: FitReport }
  | { type: "stats"; stats: GenStats };

export type ConverseResult = {
  answer: string;
  /** Every lattice row touched this turn, for the constellation to flare. */
  rowsTouched: number[];
  toolCalls: { query: string; hits: number; justification?: string }[];
  totalMs: number;
};

function toolInstructions(dialect: Dialect): string {
  // The example is a PLACEHOLDER, not a plausible query.
  //
  // It used to read "search: unified memory bandwidth on the GB10", and the
  // Gardener duly emitted that exact string when asked about blossom end rot.
  // Twice. Classic few-shot example bleed: a well-formed example sentence is
  // something a small model will happily copy instead of composing its own.
  // A slot in angle brackets cannot be mistaken for an answer.
  const example =
    dialect === "records"
      ? "search: <what you need to look up, in plain words>"
      : "<search>what you need to look up, in plain words</search>";
  return [
    `You can search your own memory. To do so, write exactly one line:`,
    ``,
    `  ${example}`,
    ``,
    `Write nothing after it and wait. The records will be given to you, and`,
    `then you answer from them. If the search returns ${EMPTY_SENTINEL}, say that`,
    `you have no record of it rather than guessing.`,
  ].join("\n");
}

const JUSTIFY_PROMPT = [
  "Before the search runs, state in one short sentence what you are looking for",
  "and why. Then write the search line.",
].join(" ");

const JUSTIFY_ESCAPE_PROMPT = [
  "Before the search runs, state in one short sentence what you are looking for",
  "and why. If on reflection you do not actually need to search, write",
  "'no search needed' instead and simply answer.",
].join(" ");

export async function converse(
  llm: MindClient,
  embedder: EmbedClient,
  pack: LoadedPack | null,
  history: ChatMessage[],
  userTurn: string,
  config: ConverseConfig = DEFAULT_CONFIG,
  onEvent?: (e: ConverseEvent) => void,
): Promise<ConverseResult> {
  const t0 = performance.now();
  const rowsTouched = new Set<number>();
  const toolCalls: ConverseResult["toolCalls"] = [];

  // Step 1: prefetch. Search before the model speaks, so the common case costs
  // no tool round trip at all.
  //
  // The query is SHAPED rather than taken literally. "please search your
  // memories for more info" has no subject in it, and embedding it verbatim
  // retrieved against the words "search" and "memories" instead of against the
  // topic, which is why that follow-up returned a byte-identical answer.
  //
  // A caller may also override the query outright. The thing worth searching
  // for is not always the thing that was just said: in a debate each speaker is
  // handed the OTHER speaker's words as its turn, and embedding those retrieves
  // whatever in its own lattice most resembles the opponent's point, so it
  // answers by paraphrasing them. Measured on the first run: the Hermeticist
  // restated the Gardener almost sentence for sentence. Searching the topic
  // instead makes each speaker fetch what IT knows about the subject.
  const priorUserTurns = history.filter((m) => m.role === "user").map((m) => m.content);
  const shaped = config.queryOverride
    ? { query: config.queryOverride, explicitSearch: false }
    : shapeQuery(userTurn, priorUserTurns);

  let opening = userTurn;
  let prefetchHit = false;
  if (pack && config.prefetch) {
    const pre = await openAperture(pack, embedder, shaped.query, config.k, config.bannerMode, config.floor);
    pre.rows.forEach((r) => rowsTouched.add(r));
    onEvent?.({ type: "prefetch", result: pre });
    if (!pre.empty) {
      // The memory comes first and the question follows plainly. The earlier
      // version ended with "Question: <turn>", which framed the whole exchange
      // as a lookup form and helped produce the citation voice.
      opening = `${pre.banner}\n\n${userTurn}`;
      prefetchHit = true;
    }
  }

  /**
   * Withhold the tool when the prefetch already answered, UNLESS the user
   * explicitly asked to go looking.
   *
   * The first half: asked what requestAnimationFrame does in a hidden tab, the
   * prefetch returned the exact answer as its top hit, and the model then
   * pasted that whole sentence back as a search query. Twice. Describing a
   * tool makes a small model want to use it.
   *
   * The second half is Free's transcript. "please search your memories for
   * more info" is a direct instruction, and the gate had removed the only
   * means of obeying it, so the model could do nothing but repeat itself. A
   * mind that is told to think harder must be able to.
   */
  const offerTool = !!pack && (!prefetchHit || shaped.explicitSearch);

  // When they have asked you to think harder, say so, or the model will
  // reasonably just restate its previous answer.
  const askedForMore = shaped.explicitSearch && prefetchHit;
  /**
   * The persona goes through the `system` prompt template.
   *
   * A WRAPPER rather than a replacement, and that is the whole design. Each
   * lattice carries its own preamble inside its .rlm, and those differing
   * preambles are a large part of why two speakers sound like two people.
   * A single editable system prompt that REPLACED them would collapse both
   * sides into one voice and quietly destroy the thing the bay demonstrates.
   *
   * The default template is bare `{preamble}`, so until someone edits it this
   * is byte-identical to what it was.
   */
  const persona = pack
    ? renderPrompt("system", {
        preamble: config.preamble ?? pack.manifest.preamble,
        persona: config.persona ?? pack.manifest.persona,
      })
    : "You are The Mind, a small model running entirely in the visitor's browser. Answer briefly and plainly. If you do not know something, say so.";

  const system: ChatMessage = {
    role: "system",
    content: pack
      ? offerTool
        ? `${persona}\n\n${toolInstructions(config.dialect)}` +
          (askedForMore
            ? `\n\nThey have asked you to think harder about this. Reach past what first` +
              ` came to mind, and do not simply repeat what you have already said.`
            : "")
        : persona
      : persona,
  };

  /**
   * Keep the conversation inside the window.
   *
   * `MAX_CONTEXT_TOKENS` had been declared since the scaffold and enforced
   * nowhere at all: chat history was `turns.map(...)` with no ceiling, so a long
   * enough conversation would simply have run off the end of the model.
   *
   * Chat DROPS rather than gists. Everything in a chat history was spoken, by
   * the visitor or by the model, and there are no think blocks to fold, so the
   * gist path finds nothing and costs no generation. Whether old chat turns
   * should be summarised the way debate notes are is a real design question and
   * a different one; dropping is the honest floor, and the event says so.
   */
  const labelled: BudgetedMsg[] = [
    { ...system, kind: "intro" },
    ...history.map<BudgetedMsg>((m) => ({ ...m, kind: "spoken" })),
    { role: "user", content: opening, kind: "spoken" },
  ];
  const fitted = await fitToBudget(
    llm,
    labelled,
    Math.max(512, MAX_CONTEXT_TOKENS - config.maxNewTokens - 256),
  );
  if (fitted.report.folded || fitted.report.dropped) {
    onEvent?.({ type: "budget", report: fitted.report });
  }
  const messages: ChatMessage[] = fitted.msgs.map(({ role, content }) => ({ role, content }));

  const stopOpts = !offerTool
    ? {}
    : config.dialect === "tag"
      ? { stopStrings: tagStopStrings() }
      : { stopAfterLine: recordsStopPrefixes() };

  // Arm B and C interrupt the instant the model commits, before the query.
  const interruptOpts = !offerTool
    ? {}
    : config.arm === "direct"
      ? stopOpts
      : config.dialect === "tag"
        ? { stopStrings: ["<search>"] }
        : { stopStrings: recordsStopPrefixes() };

  let answer = "";
  let calls = 0;

  for (;;) {
    // Drop the KV cache before every generation in this loop.
    //
    // The optimistic version kept it, on the theory that appending a tool
    // result only extends the prefix. It does not. Each turn is re-rendered
    // through the chat template, which inserts role delimiters and a fresh
    // generation prompt, so the new token sequence is not a clean extension of
    // the cached one. The observable symptom was the model going silent after
    // a tool result: the search ran, the records were injected, and the second
    // generation produced nothing.
    //
    // Correct and slower beats fast and mute. The prefill this costs is
    // measurable and can be optimised later by appending tokens directly
    // instead of re-templating, but that needs a receipt, not a hunch.
    llm.resetCache();

    const first = await llm.generate(
      messages,
      {
        ...interruptOpts,
        enableThinking: config.enableThinking,
        maxNewTokens: config.maxNewTokens,
      },
      { onToken: (t) => onEvent?.({ type: "token", text: t }) },
    );
    onEvent?.({ type: "stats", stats: first.stats });

    let text = first.text;
    // The visible answer gets prose only. A tool call is machinery, and the
    // provenance panel is where it belongs, not in the middle of a sentence.
    const firstCall = parseToolCall(first.text, config.dialect, { allowUnterminated: true });
    answer += firstCall ? first.text.slice(0, firstCall.start) : first.text;
    let justification: string | undefined;

    // Arms B and C: we stopped at the prefix. Ask for the reason, then let the
    // model complete the call in the same continuation.
    if (config.arm !== "direct" && first.stats.stoppedOn && calls < config.maxToolCalls) {
      messages.push({ role: "assistant", content: first.text });
      messages.push({
        role: "user",
        content: config.arm === "justify-escape" ? JUSTIFY_ESCAPE_PROMPT : JUSTIFY_PROMPT,
      });
      const second = await llm.generate(
        messages,
        { ...stopOpts, enableThinking: config.enableThinking, maxNewTokens: 160 },
        { onToken: (t) => onEvent?.({ type: "token", text: t }) },
      );
      onEvent?.({ type: "stats", stats: second.stats });

      const call = parseToolCall(second.text, config.dialect, { allowUnterminated: true });
      justification = (call ? second.text.slice(0, call.start) : second.text).trim();
      if (justification) onEvent?.({ type: "justification", text: justification });

      // Arm C's escape hatch: the model reconsidered, so let it answer.
      if (!call || /no search needed/i.test(second.text)) {
        messages.push({ role: "assistant", content: second.text });
        answer += "\n" + second.text.replace(/no search needed\.?/i, "").trim();
        break;
      }
      text = second.text;
    }

    const call = offerTool ? parseToolCall(text, config.dialect, { allowUnterminated: true }) : null;
    if (!call || !pack || calls >= config.maxToolCalls) {
      if (call && calls >= config.maxToolCalls) {
        // Budget spent. Say so rather than silently dropping the call.
        messages.push({ role: "assistant", content: text });
        messages.push({
          role: "user",
          content: `No further searches are available this turn. Answer from what you already have, or say you cannot.`,
        });
        const last = await llm.generate(
          messages,
          { enableThinking: config.enableThinking, maxNewTokens: config.maxNewTokens },
          { onToken: (t) => onEvent?.({ type: "token", text: t }) },
        );
        // Strip here too. A model that has just spent its budget on tool calls
        // is exactly the one likely to emit another, and this branch used to
        // append it raw, which is how a bare "search: ..." line reached the
        // visible answer.
        const lastCall = parseToolCall(last.text, config.dialect, { allowUnterminated: true });
        answer += "\n" + (lastCall ? last.text.slice(0, lastCall.start) : last.text);
      }
      break;
    }

    calls++;
    onEvent?.({ type: "toolCall", query: call.arg });

    const res = await openAperture(pack, embedder, call.arg, config.k, config.bannerMode, config.floor);
    res.rows.forEach((r) => rowsTouched.add(r));
    onEvent?.({ type: "toolResult", result: res });
    toolCalls.push({ query: call.arg, hits: res.hits.length, justification });

    // The result goes back in as a user turn, which is how the chat template
    // renders an observation the model did not author.
    messages.push({ role: "assistant", content: text });
    messages.push({
      role: "user",
      content:
        `Thinking about "${call.arg}", this comes back to you:\n\n${formatSearchResults(res.results)}\n\n` +
        `Now answer, in your own voice. Do not mention searching or recalling.`,
    });
  }

  return {
    answer: answer.trim(),
    rowsTouched: [...rowsTouched],
    toolCalls,
    totalMs: performance.now() - t0,
  };
}
